import { useCallback, useEffect, useRef, useState } from "react";
import { http } from "../utils/config";
import { cacheGet, cacheSet, cacheInvalidate, fetchDeduped } from "../utils/apiCache";

const CACHE_KEY = "store:list-all";
const TTL_MS = 5 * 60 * 1000; // 5 phút

async function fetchStoreList() {
  const res = await http.get("/mcoupon/api/v1/branchs");
  return res.data;
}

/**
 * Hook SWR cho danh sách cửa hàng/đơn vị.
 *
 * Hành vi:
 * - Có cache còn hạn → trả ngay, đồng thời fetch mới ngầm (stale-while-revalidate)
 * - Hết hạn / chưa có → fetch, trả loading=true cho đến khi xong
 * - N component dùng cùng lúc → chỉ 1 request in-flight (dedup)
 *
 * @returns {{ data: object|null, loading: boolean, refresh: () => void }}
 *   data = response.data gốc từ API (object { data: [...], ... })
 */
export default function useStoreData() {
  const cached = cacheGet(CACHE_KEY, TTL_MS);
  const [state, setState] = useState({
    data: cached?.data ?? null,
    loading: !cached,
  });

  // Ref để tránh setState sau unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const revalidate = useCallback((isBackground = false) => {
    if (!isBackground && mountedRef.current) {
      setState(prev => ({ ...prev, loading: true }));
    }

    fetchDeduped(CACHE_KEY, fetchStoreList)
      .then(data => {
        cacheSet(CACHE_KEY, data);
        if (mountedRef.current) {
          setState({ data, loading: false });
        }
      })
      .catch(() => {
        if (mountedRef.current) {
          setState(prev => ({ ...prev, loading: false }));
        }
      });
  }, []);

  useEffect(() => {
    const entry = cacheGet(CACHE_KEY, TTL_MS);
    if (entry) {
      // Cache còn hạn: trả ngay, fetch mới ngầm không hiện loading
      revalidate(true);
    } else {
      // Chưa có hoặc hết hạn: fetch bình thường
      revalidate(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(() => {
    cacheInvalidate(CACHE_KEY);
    revalidate(false);
  }, [revalidate]);

  return { data: state.data, loading: state.loading, refresh };
}
